/**
 * `catalog/posCard` — the POS's LEGACY card shape, built over the shared pipeline.
 *
 * ## Why this file exists
 *
 * Until 2026-09-04 the POS had its own catalogue projection in `packages/core/catalog` — a
 * second 13-step resolve loop, a second price function, a second schedule check and a second
 * name builder, all duplicating `./catalogModel`. Two definitions of one thing, which is
 * exactly what CLAUDE.md's "one definition, always" exists to prevent; the repo's own gates
 * could not see it, because `audit:uplift` check A compares core against the APPS, never core
 * against core.
 *
 * That folder is gone. The *rules* now live in `./catalogModel` and run once. What survives
 * here is only the thing that genuinely differs: the SHAPE of the card the POS renders.
 *
 * ## Two card shapes, one pipeline
 *
 * `resolveItems` takes a `CardBuilder`, so the filtering, pricing, schedule and naming rules
 * are shared while each surface gets the object it wants:
 *
 * | builder | shape | who renders it |
 * |---|---|---|
 * | `toCatalogItem` (./catalogModel) | 10 typed fields | kiosk, recommendations |
 * | `toPosCatalogItem` (here) | the raw item + ~20 computed fields | POS web + mobile |
 *
 * The POS shape is `...item` plus computed fields, which is why it cannot simply be derived
 * from `CatalogItem` — it carries `skuCode`, `barCode`, `measuringUnit`, `menuIds`, `tagIds`
 * and the whole raw item besides. It retires when the POS adopts the revamped ordering flow;
 * until then it is a shape, not a second implementation.
 *
 * ## Bug-for-bug on purpose
 *
 * Three quirks below are the app's own and are preserved verbatim, because this file feeds
 * live till paths and a refactor is not the place to change what a cashier sees:
 *   1. `...item` spreads LAST, so raw item keys overwrite the computed ones above them.
 *   2. `longName + " " + attrs || ""` parses as `(a + " " + b) || ""` — the `|| ""` can never
 *      fire for a variant.
 *   3. a `NaN` price is coerced to `0`, which is what makes the `validPrice` filter DEAD on
 *      every POS path. That deadness is preserved exactly: `resolveItems` tests the card's
 *      price after the builder has run, so a POS card's price is already `0` by then.
 *      `toCatalogItem` does not coerce, so `validPrice` is live for kiosk/recommendations —
 *      each surface keeps the behaviour it has today.
 */
import { tz } from "moment-timezone";

import {
  attributeSummary,
  displayNameOf,
  isItemAvailableNow,
  priceOf,
  resolveItems,
} from "./catalogModel";
import { toResolveContext } from "./context";
import type { CatalogContextInput } from "./context";
import type { CardBuilder, CatalogItem, ResolveContext, ResolveOptions } from "./types";

// Re-exported so `./posCard` stays one import for the legacy POS surface, even though neither
// belongs to this file any more. New code imports them from `./context`.
export { toResolveContext } from "./context";
export type { CatalogContextInput } from "./context";

/**
 * The POS's name for the filter flags: `ResolveOptions` minus `sort`.
 *
 * 🔴 **It was a hand-written interface repeating all six fields, names and docs.** Two
 * declarations of one list — so a filter added to `ResolveOptions` silently did not exist for
 * POS callers. Derived now, and the derivation is the documentation.
 *
 * `sort` is excluded deliberately rather than forgotten: `getCatalogItemsFromIds` forces
 * `"input"` for every POS caller (see its header), so offering the choice would be offering one
 * that is overridden.
 */
export type CatalogItemsFilterConfig = Omit<ResolveOptions, "sort">;

export const DEFAULT_FILTER_CONFIG: Required<CatalogItemsFilterConfig> = {
  resolveVariantToParent: false,
  skipVariants: false,
  scheduleAvailability: false,
  validPrice: false,
  channelEligibility: false,
  excludeSoldOuts: false,
};

export const getItemAttributes = attributeSummary;

/**
 * Resolve an item's price for a channel/branch/time. The POS's parameter shape, over the one
 * `priceOf` the kiosk uses.
 *
 * Returns the library's value unchanged — including `NaN`, which the CARD builder (not this
 * function) is what coerces to `0`. Callers that price a single item, like `CustomiseItemPopup`,
 * always saw the raw value and still do.
 */
export function getItemPrice(item: any, params: any): any {
  const { channelData, branchTimeZoneId, branchId, itemSchedules } = params ?? {};
  return priceOf(item, {
    itemsById: {},
    channel: channelData,
    branchId,
    branchTime: tz(branchTimeZoneId as string).format(),
    itemSchedules,
  } as ResolveContext);
}

/**
 * Is this item sellable right now, on this channel, at branch time?
 *
 * 🔴 **The one real behaviour change of the `catalog/` retirement, and it needs QA.** The retired
 * POS copy never forwarded `branchId` to the library's `isScheduleValidForBranchAndChannel`, so
 * that gate returned `true` for EVERY schedule whose channel matched — including schedules
 * belonging to other branches. `isItemAvailableNow` forwards it, so a schedule with a non-empty
 * `branchIds` that excludes this branch is now filtered out before the time check.
 *
 * ⚠️ **The effect runs in BOTH directions**, and the one that will actually be noticed is items
 * DISAPPEARING, not appearing. Walking `catalogutil.isItemAvailable`:
 *
 *   · An item whose only applicable schedule is foreign and out-of-hours used to fail the time
 *     check; the foreign schedule is now filtered out, no applicable schedule remains, and the
 *     function's `applicableItemSchedules.length === 0` branch returns `true`. MORE available.
 *   · An item referencing this branch's schedule AND another branch's — the normal shape for a
 *     chain sharing one catalogue with per-branch hours — used to be available whenever EITHER
 *     was in-hours. Branch X's "Breakfast 7-11" item stayed sellable at 11:30 because branch Y's
 *     "8-12" schedule was still valid. It now correctly stops at 11. LESS available.
 *
 * The second case is the bug the forwarding fixes, and it is the common one.
 *
 * Blast radius is narrow. Unaffected: merchants with a single branch, and any schedule with an
 * empty/absent `branchIds` (it passes the gate either way). Direct callers are unaffected too —
 * neither POS call site (`CustomiseItemPopup.checkItemIdInSchedule`, and the retired loop's own
 * check) ever had a `branchId` to pass. The change reaches only `getCatalogItemsFromIds` callers
 * that also switch `scheduleAvailability` ON, which today is exactly three: `ItemsView` (the main
 * POS grid), `CustomerSummary/RenderFavItemsList`, and the recommendation widget.
 * `BarCodeSearch` does not set the flag and is untouched.
 */
export function isItemAvailableInSchedule(
  itemMid: string,
  params: {
    itemsData: Record<string, any>;
    branchTimeZoneId?: string;
    channelDefId?: string;
    itemSchedules: any;
    branchId?: string;
  },
): boolean {
  const { itemsData, branchTimeZoneId, channelDefId, itemSchedules, branchId } = params;
  if (!itemsData?.[itemMid]) return false;
  return isItemAvailableNow(itemMid, {
    itemsById: itemsData,
    channel: { channelDefId } as any,
    branchId: branchId as string,
    branchTime: tz(branchTimeZoneId as string).format(),
    itemSchedules,
  } as ResolveContext);
}

/**
 * The POS's fat card — **a distinct TYPE, not `any`.**
 *
 * 🔴 This was `CardBuilder<any>`, and `any` is what made the shapes silently interchangeable.
 * `packages/ui`'s customer-summary Favourites tab was written against `CatalogItem` and fed
 * these; it read `item.isSoldOut`, which this shape spells `isInActive`, so the field was
 * `undefined` on every row — no SOLD OUT tag, and a sold-out favourite stayed addable. Nothing
 * failed, because `any[]` assigns to `CatalogItem[]` without a murmur.
 *
 * Naming it closes that. `PosCatalogItem` is missing `id`, `tag`, `isCustomisable` and
 * `isSoldOut`, so handing one to a `CatalogItem` consumer is now a compile error — verified,
 * and the index signature below does NOT weaken it (TypeScript will not satisfy a target's
 * required properties from a source's index signature).
 *
 * The index signature is honest rather than lazy: `...item` really does spread the whole raw
 * item, and AS-IS POS code really does reach through it (`BarCodeSearch` reads `info.longName`,
 * mobile's list card reads `info.skuCode` and `channelIds`).
 */
export interface PosCatalogItem {
  /** The raw item, spread LAST — see quirk 1. Typed loosely because it genuinely is the item. */
  [key: string]: any;
  /** `CatalogItem` calls this `id`. */
  mid: string;
  name: string;
  price: number;
  /** `CatalogItem` calls this `isSoldOut`. THE rename that cost a shipped bug. */
  isInActive: boolean;
  /** Together these are `CatalogItem.isCustomisable`, split in two. */
  hasItemVariants: boolean;
  hasItemOptions: boolean;
  imageDataId?: string;
  skuCode?: string;
  barCode?: string;
  measuringUnit?: string;
  menuIds?: string[];
  categoryId?: string;
  subCategoryId?: string;
  displayOrder?: number;
  type?: string;
}

/** The POS's fat card. See this file's header for the preserved quirks. */
export const toPosCatalogItem: CardBuilder<PosCatalogItem> = (item, ctx, soldOut) => {
  const baseItemDataToSet: PosCatalogItem = {
    /*
      🔴 One naming rule, not two. This built the name inline —
      `longName + " " + attrs || ""` for a variant, `longName || ""` otherwise — while
      `displayNameOf` next door did the same job. They disagreed in three ways and every
      disagreement was a defect: no `shortName` fallback (an item named only with a short name
      rendered BLANK, which the kiosk rail was separately patching around), a trailing space on
      a variant with no attributes, and the `|| ""` binding to the whole concatenation so it
      could never fire.

      Switching to `displayNameOf` is strictly an improvement in each case, never a regression:
      a name that renders today renders identically.
    */
    name: displayNameOf(item),
    imageURL: item.info.imageURL,
    imageDataId: item.info.imageDataId,
    imagePath: item.info.imagePath,
    mid: item.mid,
    skuCode: item.info.skuCode || "",
    status: item.info.status || "",
    tagIds: item.info.tagIds || [],
    measuringUnit: item.info.measuringUnit || "ea",
    barCode: item.info.barCode || "",
    isInActive: soldOut.has(item.mid),
    description: item.info.description,
    categoryId: item.info.categoryId,
    hasItemVariants: !!item.variantIds?.length,
    hasItemOptions: !!item.priceInfo?.optionSetIds?.length,
    price: 0,
    type: item.type,
    subCategoryId: item.info?.subCategoryId || "",
    menuIds: item.info?.menuIds ? [...item.info?.menuIds] : [],
    displayOrder: item?.displayOrder || undefined,
    ...item,
  };
  const price = priceOf(item, ctx);
  // Quirk 3, PRESERVED: `NaN` -> `0`, which is what makes `validPrice` dead on every POS path.
  // Live till code renders these; turning the filter on here would make items vanish from a
  // cashier's grid. It is live for `toCatalogItem`, so each lineage keeps what it has today.
  baseItemDataToSet.price = isNaN(price) ? 0 : price;
  return baseItemDataToSet;
};

/**
 * A `CatalogItem` → the minimum both apps' AS-IS `useItemSelection.handleItemClick` reads.
 *
 * THE SEAM between the shared flow and the POS's untouched item machinery. Both apps' suggestion
 * widgets render `CatalogItem`s now and both hand the tapped one to their own `useItemSelection`,
 * which was written against the legacy card — so without this each app would grow its own copy of
 * the same four-line translation, which is the duplication this whole module is trying to shed.
 *
 * It is a RENAME, not a re-derivation. `id` is `mid`, and `isCustomisable` IS the
 * `hasItemVariants || hasItemOptions` that `handleItemClick` tests — so the OR is preserved
 * exactly by putting the answer in the first operand. `measuringUnit` is deliberately absent:
 * both hooks look it up from the catalogue by `mid` (web always did; mobile was converged onto
 * it), never off the card.
 *
 * Delete it when `useItemSelection` moves onto `@rista/core/ordering/cart`.
 */
export function toLegacyClickTarget(
  item: Pick<CatalogItem, "id" | "isCustomisable"> & { recommendationInfo?: any },
): Pick<PosCatalogItem, "mid" | "hasItemVariants" | "hasItemOptions"> & {
  recommendationInfo?: any;
} {
  return {
    mid: item.id,
    hasItemVariants: item.isCustomisable,
    hasItemOptions: false,
    recommendationInfo: item.recommendationInfo,
  };
}

/**
 * Shape ONE catalogue item for display, resolving its price. The POS's parameter shape.
 *
 * ⚠️ **Returns `any`, deliberately — the same concession `useCatalogItemsFromIds` makes**, and for
 * the same reason: every caller is AS-IS release code. Annotating it `PosCatalogItem` broke
 * `CustomiseItemPopup`, which does `getVariantData(mid) || {}` and then reads `.info?.skuCode`
 * off the result. With a real return type that expression is `PosCatalogItem | {}` and neither
 * property exists on `{}`; while it was `any` the union collapsed and the `|| {}` was harmless.
 *
 * The type would be reporting a real hazard in code nobody may touch. `getCatalogItemsFromIds`
 * and `toPosCatalogItem` above KEEP their types — their callers are all inside `packages/core`,
 * where the boundary is worth enforcing.
 *
 * `soldOutItemsList` reaches the builder as the Set the pipeline would have built, so a lone
 * call and a batch call produce identical cards.
 */
export function getItemDataForCatalog(item: any, params: any): any {
  const context: CatalogContextInput = {
    itemsData: {},
    selectedChannel: params?.channelData,
    itemSchedules: params?.itemSchedules,
    branchId: params?.branchId,
    branchTimeZoneId: params?.branchTimeZoneId,
    soldOutItemsList: params?.soldOutItemsList,
  };
  return toPosCatalogItem(
    item,
    toResolveContext(context),
    new Set(params?.soldOutItemsList ?? []),
  );
}

/**
 * Display-ready POS cards for `itemIds`, filtered per `filterConfig`.
 *
 * ⚠️ `sort: "input"` is REQUIRED here, not incidental: the retired POS loop never sorted, and
 * `resolveItems` defaults to menu order. Two of this function's callers pass ranked ids — the
 * recommendation widget most obviously — so sorting would change which items survive a cap.
 */
export function getCatalogItemsFromIds(
  itemIds: string[],
  context: CatalogContextInput,
  filterConfig: CatalogItemsFilterConfig = {},
): PosCatalogItem[] {
  const options: ResolveOptions = { ...DEFAULT_FILTER_CONFIG, ...filterConfig, sort: "input" };
  // The retired loop turned `skipVariants` OFF when both were set; `resolveItems` expresses the
  // same precedence inline (`skipVariants && !resolveVariantToParent`), so nothing is needed here.
  return resolveItems(itemIds, toResolveContext(context), options, toPosCatalogItem);
}
